import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, conceptMatchesCategory } from "../lib/categories";
import PullToRefresh from "../components/PullToRefresh";
import TraditionsTabs from "../components/TraditionsTabs";
const SOURCE_TYPES = ["academic", "esoteric", "speculative", "redacted"];

export default function Archive() {
  const [concepts, setConcepts] = useState([]);
  const [traditions, setTraditions] = useState([]);
  const [q, setQ] = useState("");
  const [tradition, setTradition] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [category, setCategory] = useState(null);

  useEffect(() => {
    api.get("/traditions").then(({ data }) => setTraditions(data)).catch((err) => console.error("traditions fetch failed", err));
  }, []);

  const fetchConcepts = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (tradition) params.tradition = tradition;
    if (sourceType) params.source_type = sourceType;
    return api.get("/concepts", { params }).then(({ data }) => setConcepts(data));
  }, [q, tradition, sourceType]);

  useEffect(() => {
    fetchConcepts().catch((err) => console.error("concepts fetch failed", err));
  }, [fetchConcepts]);

  const visible = concepts.filter((c) => conceptMatchesCategory(c, category));

  // Soft banner: shown after 2+ reads, if user hasn't dismissed it.
  const [showTune, setShowTune] = useState(false);
  const [recs, setRecs] = useState([]);
  const fetchRecs = useCallback(
    () => api.get("/recommendations/feed").then(({ data }) => setRecs(data || [])).catch(() => {}),
    [],
  );
  useEffect(() => {
    try {
      const reads = JSON.parse(localStorage.getItem("cor_reads") || "[]");
      const dismissed = localStorage.getItem("cor_tune_dismissed") === "1";
      if (reads.length >= 2 && !dismissed) setShowTune(true);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[archive] soft-fail:", e?.message); }
    fetchRecs();
  }, [fetchRecs]);
  const dismissTune = () => { localStorage.setItem("cor_tune_dismissed", "1"); setShowTune(false); };

  const onPullRefresh = useCallback(async () => {
    await Promise.allSettled([fetchConcepts(), fetchRecs()]);
  }, [fetchConcepts, fetchRecs]);

  return (
    <PullToRefresh onRefresh={onPullRefresh}>
    <div data-testid="archive-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
      <TraditionsTabs />
      {showTune && (
        <div data-testid="tune-banner" className="mb-10 p-6 hairline flex flex-wrap items-center justify-between gap-4">
          <p className="serif italic text-foreground/85 max-w-2xl">
            You've read a few. Want recommendations tuned to where you're coming from?
          </p>
          <div className="flex gap-3">
            <Link to="/settings" className="btn-gold" data-testid="tune-go">Tune your archive</Link>
            <button onClick={dismissTune} data-testid="tune-dismiss" className="btn-ghost">Not now</button>
          </div>
        </div>
      )}
      <div className="label mb-6 text-[hsl(var(--accent))]">The Archive</div>
      <h1 className="serif text-5xl sm:text-6xl text-foreground max-w-3xl">
        {category ? category.title : "Where would you like to begin?"}
      </h1>
      <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
        {category
          ? category.blurb + " Pick any thread — they all lead, eventually, to one another."
          : "Choose a doorway. Each one opens onto the same room from a different side. Nothing is wrong; nothing is final; everything can be returned to."}
      </p>

      {/* CATEGORY GRID */}
      {!category && (
        <>
          {recs.length > 0 && (
            <section className="mt-16" data-testid="recommended-feed">
              <div className="flex items-end justify-between mb-6">
                <div className="label text-[hsl(var(--primary))]">Recommended for you</div>
                <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                  {recs.length} threads
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                {recs.slice(0, 6).map((r) => (
                  <Link
                    key={r.concept_id}
                    to={`/concept/${r.concept_id}`}
                    data-testid={`rec-${r.concept_id}`}
                    className="paper p-6 hover:bg-secondary/40 transition-colors duration-300 block group"
                  >
                    <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-3">
                      {r.reason}
                    </div>
                    <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors leading-tight">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-2">{r.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-16">
            <div className="label mb-4 text-[hsl(var(--accent))]">Or pick a doorway</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="category-grid">
          {CATEGORIES.map((cat, i) => {
            const count = concepts.filter((c) => conceptMatchesCategory(c, cat)).length;
            return (
              <button
                key={cat.id}
                data-testid={`category-${cat.id}`}
                onClick={() => setCategory(cat)}
                className="bg-card p-8 sm:p-10 text-left hover:bg-secondary transition-colors duration-500 group"
              >
                <div className="flex items-baseline justify-between mb-5">
                  <span className="serif text-3xl text-muted-foreground/30 group-hover:text-[hsl(var(--primary))] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
                    {count} {count === 1 ? "concept" : "concepts"}
                  </span>
                </div>
                <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--accent))] transition-colors leading-tight">
                  {cat.title}
                </h3>
                <p className="mt-3 text-muted-foreground text-[14px] leading-relaxed">{cat.blurb}</p>
              </button>
            );
          })}
          <button
            data-testid="category-all"
            onClick={() => setCategory({ id: "all", title: "The whole archive", blurb: "Every concept, every tradition, all at once.", traditions: [] })}
            className="bg-card p-8 sm:p-10 text-left hover:bg-secondary transition-colors duration-500 group"
          >
            <div className="flex items-baseline justify-between mb-5">
              <span className="serif text-3xl text-muted-foreground/30 group-hover:text-[hsl(var(--primary))] transition-colors">∞</span>
              <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">{concepts.length} concepts</span>
            </div>
            <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--accent))] transition-colors leading-tight">
              Show me everything
            </h3>
            <p className="mt-3 text-muted-foreground text-[14px] leading-relaxed">No filter. Let the patterns find you.</p>
          </button>
        </div>
        </>
      )}

      {/* CATEGORY CHOSEN: filters + list */}
      {category && (
        <>
          <div className="mt-10 flex items-center gap-6">
            <button
              onClick={() => { setCategory(null); setTradition(""); setSourceType(""); setQ(""); }}
              data-testid="back-to-categories"
              className="mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground link-underline"
            >
              ← All doorways
            </button>
            <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
              {visible.length} {visible.length === 1 ? "concept" : "concepts"}
            </span>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 pb-10 hairline-b">
            <div>
              <div className="label mb-3">Search</div>
              <input
                data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="logos, kundalini, vimana…" className="input-line w-full mono text-sm"
              />
            </div>
            <div>
              <div className="label mb-3">Tradition</div>
              <select
                data-testid="archive-filter-tradition" value={tradition}
                onChange={(e) => setTradition(e.target.value)}
                className="input-line w-full mono text-sm appearance-none cursor-pointer"
              >
                <option value="">All in {category.title.toLowerCase()}</option>
                {traditions.map((t) => (
                  <option key={t} value={t} className="bg-card">{t}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label mb-3">Source class</div>
              <div className="flex flex-wrap gap-2">
                {SOURCE_TYPES.map((s) => (
                  <button
                    key={s} data-testid={`archive-filter-source-${s}`}
                    onClick={() => setSourceType(sourceType === s ? "" : s)}
                    className={`tag tag-${s} ${sourceType === s ? "ring-1 ring-foreground/30" : "opacity-70"} hover:opacity-100`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
            {visible.map((c) => (
              <Link
                key={c.id} to={`/concept/${c.id}`} data-testid={`concept-card-${c.id}`}
                className="bg-card p-10 hover:bg-secondary transition-colors duration-500 group block"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className={`tag tag-${c.source_type}`}>{c.source_type}</span>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/55">
                    {c.traditions?.length} parallels
                  </span>
                </div>
                <h2 className="serif text-3xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors leading-tight">
                  {c.title}
                </h2>
                <p className="mt-4 text-muted-foreground leading-relaxed text-[15px]">{c.summary}</p>
                <div className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-2">
                  {c.traditions?.slice(0, 6).map((t, i) => (
                    <span key={i} className="text-sm text-muted-foreground">
                      <span className="text-muted-foreground/55 mono text-[10px] uppercase tracking-wider mr-1">{t.tradition}</span>
                      <span className="serif italic">{t.term}</span>
                    </span>
                  ))}
                </div>
              </Link>
            ))}
            {visible.length === 0 && (
              <div className="bg-card p-16 col-span-full text-center text-muted-foreground/75 italic serif text-xl">
                Nothing matches yet — try another doorway, or clear the filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </PullToRefresh>
  );
}
